// apps/web/src/lib/compras/pedido-form.ts — Fase 13, Plano 19 (COMP-02, D-14, D-20).
// Regras puras do formulário de pedido de compra: validação com as mensagens do UI-SPEC, prazo geral que só preenche
// itens sem prazo, total ao vivo e texto de conversão unidade de compra -> unidade de estoque.
// Quem manda na verdade é o servidor (revalida > 0, >= 0 e 4 casas); aqui só se evita o ida e volta inútil.
import type {
  CriarPedidoCompraInput,
  EditarPedidoCompraInput,
  MaterialLookupCompras,
  PedidoCompraItemView,
  PedidoCompraView,
} from '@pieltec/contracts';
import { interpretarNumeroPtBr, mensagemNumeroAmbiguo } from '@pieltec/contracts';

export const MENSAGEM_ESCOLHA_FORNECEDOR = 'Escolha o fornecedor.';
export const MENSAGEM_SEM_ITENS = 'Adicione ao menos um item.';
export const MENSAGEM_QUANTIDADE = 'A quantidade deve ser maior que zero.';
export const MENSAGEM_PRECO_NEGATIVO = 'O preço não pode ser negativo.';
export const MENSAGEM_INFORME_MATERIA_PRIMA = 'Informe a matéria-prima.';
export const MENSAGEM_PRECO_OBRIGATORIO = 'Informe o preço unitário.';
export const MENSAGEM_CASAS_QUANTIDADE = 'Use até 4 casas decimais na quantidade.';
export const MENSAGEM_CASAS_PRECO = 'Use até 4 casas decimais no preço.';
export const AJUDA_PRAZO_GERAL = 'Preenche o prazo dos itens. Você pode ajustar item a item.';
export const ALERTA_SEM_FATOR =
  'Esta matéria-prima não tem fator de conversão. O pedido é salvo, mas o recebimento ficará bloqueado até cadastrar o fator.';

/** Máximo de itens por pedido na API (T-13-15G). */
export const MAXIMO_ITENS_PEDIDO = 500;

/** Item em edição no formulário (números já interpretados; NaN nunca chega aqui: a validação recusa antes). */
export interface ItemPedidoCompraForm {
  /** Presente quando o item já existe no servidor (edição do pedido). */
  readonly id?: string;
  readonly material: MaterialLookupCompras;
  readonly codigoFornecedor: string | null;
  /** Na unidade de compra. */
  readonly quantidade: number;
  readonly precoUnitario: number;
  /** AAAA-MM-DD ou null. */
  readonly prazoEntrega: string | null;
  /** Só na edição: quanto o item já recebeu (limita remoção, troca de matéria-prima e redução de quantidade). */
  readonly recebido?: number;
  /** Item encerrado/cancelado: fica no pedido (a lista enviada é a completa) mas sem ações. */
  readonly somenteLeitura?: boolean;
}

export type CampoItemPedido = 'material' | 'quantidade' | 'preco';
export type ErrosItemPedido = Partial<Record<CampoItemPedido, string>>;

/** Ordem visual dos campos do Sheet de item (o primeiro inválido recebe o foco). */
export const ORDEM_ITEM_PEDIDO: readonly CampoItemPedido[] = ['material', 'quantidade', 'preco'];

function temMaisCasasQue(valor: number, casas: number): boolean {
  const fator = 10 ** casas;
  return Math.abs(valor * fator - Math.round(valor * fator)) > 1e-6;
}

export interface EntradaItemPedido {
  readonly materialId: string | null | undefined;
  readonly quantidadeTexto: string;
  readonly precoTexto: string;
}

/** Interpreta o texto digitado como número pt-BR (NaN quando vazio, inválido ou ambíguo). */
export function lerNumero(texto: string): number {
  const resultado = interpretarNumeroPtBr(texto);
  return resultado.tipo === 'ok' ? resultado.valor : Number.NaN;
}

function mensagemDoTexto(texto: string, padrao: string): string {
  const resultado = interpretarNumeroPtBr(texto);
  return resultado.tipo === 'ambiguo' ? mensagemNumeroAmbiguo(texto, resultado) : padrao;
}

/** Validação do item (Sheet): matéria-prima, quantidade > 0 (até 4 casas) e preço >= 0 (até 4 casas). */
export function validarItemPedidoCompra(entrada: EntradaItemPedido): ErrosItemPedido {
  const erros: ErrosItemPedido = {};
  if (!entrada.materialId) erros.material = MENSAGEM_INFORME_MATERIA_PRIMA;

  const quantidade = lerNumero(entrada.quantidadeTexto);
  if (!Number.isFinite(quantidade) || quantidade <= 0) erros.quantidade = mensagemDoTexto(entrada.quantidadeTexto, MENSAGEM_QUANTIDADE);
  else if (temMaisCasasQue(quantidade, 4)) erros.quantidade = MENSAGEM_CASAS_QUANTIDADE;

  const preco = lerNumero(entrada.precoTexto);
  if (entrada.precoTexto.trim() === '') erros.preco = MENSAGEM_PRECO_OBRIGATORIO;
  else if (!Number.isFinite(preco)) erros.preco = mensagemDoTexto(entrada.precoTexto, MENSAGEM_PRECO_OBRIGATORIO);
  else if (preco < 0) erros.preco = MENSAGEM_PRECO_NEGATIVO;
  else if (temMaisCasasQue(preco, 4)) erros.preco = MENSAGEM_CASAS_PRECO;
  return erros;
}

export interface ErrosPedidoCompra {
  fornecedorId?: string;
  itens?: string;
}

export interface EntradaPedidoCompra {
  readonly fornecedorId: string | null | undefined;
  readonly totalItens: number;
}

/** Validação do cabeçalho + itens: "Escolha o fornecedor." e "Adicione ao menos um item.". */
export function validarPedidoCompra(entrada: EntradaPedidoCompra): ErrosPedidoCompra {
  const erros: ErrosPedidoCompra = {};
  if (!entrada.fornecedorId) erros.fornecedorId = MENSAGEM_ESCOLHA_FORNECEDOR;
  if (entrada.totalItens <= 0) erros.itens = MENSAGEM_SEM_ITENS;
  return erros;
}

/**
 * Prazo geral: preenche SÓ os itens sem prazo; um prazo já ajustado item a item nunca é sobrescrito (D-20).
 * Prazo geral vazio não altera nada.
 */
export function aplicarPrazoGeral<T extends { readonly prazoEntrega: string | null }>(itens: readonly T[], prazoGeral: string | null | undefined): T[] {
  if (!prazoGeral) return [...itens];
  return itens.map((item) => (item.prazoEntrega ? item : { ...item, prazoEntrega: prazoGeral }));
}

/** Total do pedido em reais (centavos somados em inteiro para não acumular erro de ponto flutuante). */
export function totalDoPedido(itens: ReadonlyArray<{ readonly quantidade: number; readonly precoUnitario: number }>): number {
  let somaCentavos = 0;
  for (const item of itens) {
    // quantidade (4 casas) x preço (4 casas) -> 8 casas; arredonda cada linha a 2 casas (total do item).
    somaCentavos += Math.round(item.quantidade * item.precoUnitario * 100);
  }
  return somaCentavos / 100;
}

const FORMATO_CONVERSAO = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 4 });

/** "= 200 kg" (quantidade convertida na unidade de estoque); sem fator ou sem quantidade válida devolve null. */
export function textoConversao(quantidade: number, fator: number | null | undefined, unidadeEstoque: string): string | null {
  if (fator === null || fator === undefined || !Number.isFinite(fator) || fator <= 0) return null;
  if (!Number.isFinite(quantidade) || quantidade <= 0) return null;
  // Evita ruído de ponto flutuante (0.1 x 3): arredonda a 4 casas, a mesma escala do servidor.
  const convertida = Math.round(quantidade * fator * 10_000) / 10_000;
  return `= ${FORMATO_CONVERSAO.format(convertida)} ${unidadeEstoque}`;
}

/** A matéria-prima tem unidade de compra diferente da de estoque e nenhum fator: a tela alerta. */
export function materialSemFator(material: Pick<MaterialLookupCompras, 'unidadeCompra' | 'unidadeEstoque' | 'fatorConversao'>): boolean {
  const compra = (material.unidadeCompra ?? '').trim().toLowerCase();
  if (compra === '' || compra === material.unidadeEstoque.trim().toLowerCase()) return false;
  return material.fatorConversao === null || material.fatorConversao <= 0;
}

/** Fator efetivo do item no formulário: igual unidade (ou sem unidade de compra) vale 1; senão o do cadastro. */
export function fatorEfetivo(material: Pick<MaterialLookupCompras, 'unidadeCompra' | 'unidadeEstoque' | 'fatorConversao'>): number | null {
  const compra = (material.unidadeCompra ?? '').trim().toLowerCase();
  if (compra === '' || compra === material.unidadeEstoque.trim().toLowerCase()) return 1;
  return material.fatorConversao !== null && material.fatorConversao > 0 ? material.fatorConversao : null;
}

/** Unidade de compra exibida (somente leitura): a do cadastro ou, sem ela, a de estoque. */
export function unidadeDeCompra(material: Pick<MaterialLookupCompras, 'unidadeCompra' | 'unidadeEstoque'>): string {
  const compra = (material.unidadeCompra ?? '').trim();
  return compra === '' ? material.unidadeEstoque : compra;
}

// ---------------------------------------------------------------------------------------------------------------------
// Edição (Task 2): o que um item que já recebeu mercadoria permite (espelha ITEM_COM_RECEBIMENTO do servidor).
// ---------------------------------------------------------------------------------------------------------------------

/** Item encerrado (saldo encerrado) ou cancelado não recebe ação: fica em leitura. */
export function itemEditavel(item: Pick<PedidoCompraItemView, 'situacao' | 'saldoEncerrado'>): boolean {
  return item.situacao !== 'CANCELADO' && item.situacao !== 'ENCERRADO' && !item.saldoEncerrado;
}

/** Item com mercadoria recebida não pode ser removido do pedido (o servidor devolve 409 ITEM_COM_RECEBIMENTO). */
export function itemRemovivel(item: Pick<ItemPedidoCompraForm, 'recebido'>): boolean {
  return !(item.recebido !== undefined && item.recebido > 0);
}

/** Quantidade mínima aceita para o item: não pode ficar abaixo do recebido. */
export function quantidadeMinimaItem(item: Pick<ItemPedidoCompraForm, 'recebido'>): number {
  return item.recebido ?? 0;
}

/**
 * Bloqueio no cliente das alterações em item com recebimento, com a mesma mensagem do servidor.
 * `anterior` = item como veio do servidor; `proximo` = como ficou no formulário. null quando permitido.
 */
export function bloqueioItemComRecebimento(
  numeroItem: number,
  anterior: Pick<ItemPedidoCompraForm, 'recebido' | 'material'>,
  proximo: Pick<ItemPedidoCompraForm, 'quantidade' | 'material'> | null,
): string | null {
  const recebido = anterior.recebido ?? 0;
  if (recebido <= 0) return null;
  if (proximo === null) return `O item ${numeroItem} já tem recebimento e não pode ser removido.`;
  if (proximo.material.id !== anterior.material.id) return `O item ${numeroItem} já tem recebimento: não é possível trocar a matéria-prima.`;
  if (Math.round(proximo.quantidade * 10_000) < Math.round(recebido * 10_000)) {
    return `O item ${numeroItem} já recebeu ${FORMATO_CONVERSAO.format(recebido)}: a quantidade não pode ficar abaixo do recebido.`;
  }
  return null;
}

/** Item do servidor -> item do formulário (para editar o pedido). */
export function itemFormDoServidor(item: PedidoCompraItemView): ItemPedidoCompraForm {
  return {
    id: item.id,
    material: {
      id: item.materialId,
      codigo: item.materialCodigo,
      nome: item.materialNome,
      unidadeEstoque: item.unidadeEstoque,
      unidadeCompra: item.unidadeCompra,
      fatorConversao: item.fatorConversao,
      codigoFornecedor: item.codigoFornecedor,
    },
    codigoFornecedor: item.codigoFornecedor,
    quantidade: item.quantidade,
    precoUnitario: item.precoUnitario,
    prazoEntrega: item.prazoEntrega,
    recebido: item.recebido,
    ...(itemEditavel(item) ? {} : { somenteLeitura: true }),
  };
}

export interface CabecalhoPedidoForm {
  readonly fornecedorId: string;
  readonly data: string;
  readonly prazoGeral: string;
  readonly observacoes: string;
}

/** Corpo do POST de criação. Dados só de tela (material, recebido) não vão ao servidor (whitelist estrita). */
export function montarCriacaoPedido(cabecalho: CabecalhoPedidoForm, itens: readonly ItemPedidoCompraForm[]): CriarPedidoCompraInput {
  return {
    fornecedorId: cabecalho.fornecedorId,
    ...(cabecalho.data ? { data: cabecalho.data } : {}),
    prazoGeral: cabecalho.prazoGeral || null,
    observacoes: cabecalho.observacoes.trim() || null,
    itens: itens.map((item) => ({
      materialId: item.material.id,
      codigoFornecedor: item.codigoFornecedor?.trim() || null,
      quantidade: item.quantidade,
      precoUnitario: item.precoUnitario,
      prazoEntrega: item.prazoEntrega || null,
    })),
  };
}

/** Corpo do PATCH de edição: lista COMPLETA de itens (ausentes são removidos) e a versão lida (lock otimista). */
export function montarEdicaoPedido(
  versao: number,
  cabecalho: Pick<CabecalhoPedidoForm, 'prazoGeral' | 'observacoes'>,
  itens: readonly ItemPedidoCompraForm[],
): EditarPedidoCompraInput {
  return {
    versao,
    prazoGeral: cabecalho.prazoGeral || null,
    observacoes: cabecalho.observacoes.trim() || null,
    itens: itens.map((item) => ({
      ...(item.id ? { id: item.id } : {}),
      materialId: item.material.id,
      codigoFornecedor: item.codigoFornecedor?.trim() || null,
      quantidade: item.quantidade,
      precoUnitario: item.precoUnitario,
      prazoEntrega: item.prazoEntrega || null,
    })),
  };
}

/** Pedido do servidor -> estado do formulário de edição. */
export function formDoPedido(pedido: PedidoCompraView): { cabecalho: CabecalhoPedidoForm; itens: ItemPedidoCompraForm[] } {
  return {
    cabecalho: {
      fornecedorId: pedido.fornecedorId,
      data: pedido.data,
      prazoGeral: pedido.prazoGeral ?? '',
      observacoes: pedido.observacoes ?? '',
    },
    // A lista enviada no PATCH é a COMPLETA: itens encerrados continuam nela (em leitura), senão o servidor os removeria.
    itens: pedido.itens.map(itemFormDoServidor),
  };
}
